import * as React from 'react';
import * as ReactDOM from 'react-dom';

import { AsyncData, onPageDidMount, onPageWillUnmount } from '../../../redux/core';
import { DefaultButton, Dialog, DialogFooter, DialogType, Dropdown, IDropdownOption, PrimaryButton } from '@fluentui/react';
import { Provider as ProviderContext, connect } from 'react-redux';
import { TimeSlot, Visit } from '../../../redux/visits/types';

import { Action } from 'redux';
import BlockUi from 'react-block-ui';
import { Dispatch } from 'redux';
import { HealAPI } from '../../../healApi';
import { RootState } from '../../../redux/types';
import { StatusOptions } from '../../../types';
import { ToastParams } from '../../../redux/message/types';
import { bindActionCreators } from 'redux';
import { store } from '../../../redux/store';
import { toast } from '../../../redux/message/actions';

export interface OwnProps {
  visit: Visit;
  onClosed?: (success: boolean) => void;
}
interface StateProps {
  availableOnSiteTimeSlots: AsyncData<TimeSlot[]>;
}
interface DispatchProps {
  actions: {
    toast: (params: ToastParams) => void;
    onPageDidMount: (name: string) => void;
    onPageWillUnmount: (name: string) => void;
  };
}

export type UpdateTimeDialogProps = OwnProps & StateProps & DispatchProps;

export interface UpdateTimeDialogState {
  date?: Date;
  timeSlot?: TimeSlot;
  timeSlotId: string;
  isSaving: boolean;
}

export class UpdateTimeDialog {
  public static show(options: OwnProps) {
    const dialogNode = document.createElement('div');
    const close = (): void => {
      ReactDOM.unmountComponentAtNode(dialogNode);
      dialogNode.remove();
    };
    ReactDOM.render(
      <ProviderContext store={store}>
        <UpdateTimeDialogInternal {...options} close={close} />
      </ProviderContext>,
      dialogNode
    );
  }
}

class Component extends React.Component<UpdateTimeDialogProps & { close: () => void }, UpdateTimeDialogState> {
  constructor(props: UpdateTimeDialogProps & { close: () => void }) {
    super(props);
    this.state = {
      timeSlotId: this.props.visit.timeSlotId,
      isSaving: false,
    };
  }

  public componentDidMount() {
    this.props.actions.onPageDidMount('UpdateTimeDialog');
  }

  public componentWillUnmount() {
    this.props.actions.onPageWillUnmount('UpdateTimeDialog');
  }

  public render(): JSX.Element {
    return (
      <Dialog
        hidden={false}
        dialogContentProps={{
          type: DialogType.largeHeader,
          title: 'Edit Visit Schedule for: ' + this.props.visit.buildPatientFullName,
        }}
        onDismiss={() => this.close()}
        modalProps={{
          isBlocking: true,
        }}
      >
        <BlockUi tag="div" blocking={this.state.isSaving || this.props.availableOnSiteTimeSlots.isFetching}>
          <Dropdown
            label="Select time slot"
            selectedKey={this.state.timeSlotId}
            options={this.timeSlotsForDropdown(this.props.availableOnSiteTimeSlots.data)}
            onChange={this.onTimeSlotChange}
          />
          <DialogFooter>
            <PrimaryButton onClick={this.onUpdateTime} disabled={!this.isValid()}>
              Ok
            </PrimaryButton>
            <DefaultButton onClick={() => this.close()}>Cancel</DefaultButton>
          </DialogFooter>
        </BlockUi>
      </Dialog>
    );
  }

  private timeSlotsForDropdown(timeSlots?: TimeSlot[]): IDropdownOption[] {
    if (!timeSlots || timeSlots.length === 0) {
      return [];
    }
    return timeSlots.slice(0, Math.min(timeSlots.length, 500)).map((timeSlot) => {
      return {
        key: timeSlot.id,
        text: timeSlot.longDateString + ' ' + timeSlot.longTimeString,
      };
    });
  }

  private onTimeSlotChange = (event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption, index?: number) => {
    if (option) {
      const timeSlot = index !== undefined ? this.props.availableOnSiteTimeSlots.data![index] : undefined;
      this.setState({ timeSlotId: option.key as string, timeSlot });
    }
  };

  private isValid() {
    return true;
  }

  private close(success = false) {
    if (this.props.onClosed) {
      this.props.onClosed(success);
    }
    this.props.close();
  }

  private onUpdateTime = () => {
    if (this.isValid()) {
      this.setState({ isSaving: true });
      HealAPI.updateTime(this.props.visit.visitCode, this.state.timeSlotId)
        .then((response) => {
          this.setState({ isSaving: false });
          if (response.ok && response.data && response.data.status === StatusOptions.OK) {
            this.props.actions.toast({
              type: 'success',
              title: 'Success',
              content: 'This visit has been successfully rescheduled.',
            });
            this.close(true);
          }
        })
        .catch((e) => {
          this.setState({ isSaving: false });
          this.props.actions.toast({
            type: 'error',
            title: 'Error',
            content: e || 'Could not reschedule the visit. Please try again later',
          });
        });
    }
  };
}

export function mapStateToProps(state: RootState): StateProps {
  return {
    availableOnSiteTimeSlots: state.onSiteManager.onSiteVisits.availableOnSiteTimeSlots,
  };
}

export function mapDispatchToProps(dispatch: Dispatch<Action>): DispatchProps {
  return {
    actions: bindActionCreators({ toast, onPageDidMount, onPageWillUnmount }, dispatch),
  };
}

export const UpdateTimeDialogInternal = connect(mapStateToProps, mapDispatchToProps)(Component);
